import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ENTRA_LOGIN_URL } from '../config/api';
import { useAuth } from '../auth/AuthContext';
import { roleHome } from '../auth/roles';

// Mirrors the codes /auth/callback sends back as `?auth_error=<code>`
// (BACKEND_API_FLOW.md §3.2) when it 302s a failed sign-in here instead
// of showing Django's own error page.
const AUTH_ERROR_MESSAGES = {
  invalid_state: "That sign-in link expired or was already used — hit \"Sign in with Microsoft\" again.",
  login_failed: 'Microsoft could not complete the sign-in. Please try again.',
  not_provisioned: "That email hasn't been invited to this portal. Contact an admin to request access.",
  deactivated: 'This account has been deactivated. Contact an admin if this seems wrong.',
  identity_mismatch: 'This email is already linked to a different Microsoft account. Contact an admin.',
};

/* Front page — brand, what the console does, and a single way in.
   Mirrors the "landingPane" screen from the ICIB Admin Console UI sample.
   The Microsoft button is a real link to the backend's GET /auth/login
   (README §4.1), which redirects to Microsoft Entra ID. No password is
   ever collected here — Entra confirms identity, then redirects back.

   Also doubles as the landing spot for both ends of that round trip:
   a signed-in session (GET /auth/me came back 200) sends the browser
   straight on to that role's portal, and a failed one shows the
   `auth_error` code the backend attached to the redirect. */
function LandingPage() {
  const { status, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authError = searchParams.get('auth_error');

  useEffect(() => {
    if (status === 'authenticated' && user) {
      navigate(roleHome(user.role), { replace: true });
    }
  }, [status, user, navigate]);

  return (
    <div className="auth-shell">
      <div className="landing-card">
        <div className="landing-copy">
          {/* Brandmark — CSS approximation of the ICIB Brokerweb logo
              (icib.co.nz); there's no source asset in this repo, so this
              recreates the lockup instead of an <img>. See .brandmark* in
              theme.css. */}
          <div className="brandmark" role="img" aria-label="ICIB Brokerweb — Insurance & Risk Advisory">
            <div className="brandmark-word">
              <span className="brandmark-icib">
                <span className="brandmark-accent" aria-hidden="true" />
                ÍCIB
              </span>
              <span className="brandmark-div" aria-hidden="true" />
              <span className="brandmark-broker">BROKERWEB</span>
            </div>
            <div className="brandmark-tag">Insurance &amp; Risk Advisory</div>
          </div>

          {authError && (
            <div className="note bad" role="alert" style={{ marginBottom: 18 }}>
              <b>Sign-in didn't go through</b>
              {AUTH_ERROR_MESSAGES[authError] ||
                'Something went wrong while signing you in — please try again.'}
            </div>
          )}

          <a className="ms-btn landing" href={ENTRA_LOGIN_URL}>
            <span className="ms-mark sm" aria-hidden="true">
              <i></i><i></i><i></i><i></i>
            </span>
            Sign in with Microsoft
          </a>

          <div className="landing-features">
            <div className="landing-feat">
              <span className="num">01</span>
              <div>
                <b>Register</b>
                <span>Every person covered, and what they submitted.</span>
              </div>
            </div>
            <div className="landing-feat">
              <span className="num">02</span>
              <div>
                <b>Movements</b>
                <span>Additions, changes and removals — one file, checked whole.</span>
              </div>
            </div>
            <div className="landing-feat">
              <span className="num">03</span>
              <div>
                <b>Configuration</b>
                <span>Companies, accounts, intake forms and products.</span>
              </div>
            </div>
          </div>

          <p className="landing-foot">
            No password is kept in this system. Checking who you are belongs
            to Microsoft — sign in with your ICIB Brokerweb work account.
          </p>
        </div>
      </div>
      <div className="auth-meta">Employee Benefits Portal · ICIB Brokerweb</div>
    </div>
  );
}

export default LandingPage;
